import { useMatchRoute } from "@tanstack/react-router"

import type { FlowStateMachine } from "@/lib/flows/base/types"

import type { createMachine, StateValueFrom } from "xstate"

export const initialStateFromRouteMatch = <TMachine extends FlowStateMachine>(
  flowMachine: TMachine,
  matchRoute: ReturnType<typeof useMatchRoute>,
) => {
  for (const [stateValue, stateNode] of Object.entries(flowMachine.states)) {
    if (
      stateNode.meta?.navigateOptions &&
      matchRoute({ to: stateNode.meta.navigateOptions.to })
    ) {
      return stateValue as StateValueFrom<TMachine>
    }
  }

  return flowMachine.config.initial as StateValueFrom<TMachine>
}

export function createMachineWithInitialState<
  TMachine extends FlowStateMachine,
>(
  machine: TMachine,
  getInitialValue: (
    input: Parameters<typeof createMachine>,
  ) => string | undefined,
): TMachine {
  if (getInitialValue === undefined) {
    // If no initial value function is provided, return the machine as is
    // so the default initial state is used
    return machine
  }

  const originalGetInitialSnapshot = machine.getInitialSnapshot

  machine.getInitialSnapshot = function (args, input) {
    const snapshot = originalGetInitialSnapshot.call(this, args, input)

    const initialValue = getInitialValue(input)

    if (initialValue) {
      return machine.resolveState({ ...snapshot, value: initialValue })
    }

    return snapshot
  }

  return machine
}
